<template>
	<div class="map-search-wrapper">
		<!-- Trigger Button -->
		<button
			v-if="!isExpanded"
			class="search-trigger-btn"
			@click="openSearch"
			title="Cerca un luogo"
		>
			<Search :size="20" />
		</button>

		<!-- Search Overlay -->
		<Teleport to="body">
			<Transition name="search-panel-fade">
				<div
					v-if="isExpanded"
					class="search-panel-overlay"
					@click.self="closeSearch"
				>
					<div class="search-container">
						<div class="search-input-wrapper">
							<Search class="search-icon" :size="20" />
							<input
								ref="inputRef"
								v-model="query"
								type="text"
								placeholder="Cerca un luogo o coordinate (Lat, Lon)..."
								class="search-input"
								@keydown.enter="handleEnter"
								@keydown.esc="closeSearch"
							/>
							<button
								v-if="query"
								class="clear-btn"
								@click="clearSearch"
								title="Cancella"
							>
								<X :size="18" />
							</button>
							<div class="divider"></div>
							<button
								class="close-panel-btn"
								@click="closeSearch"
								title="Chiudi"
							>
								<X :size="20" />
							</button>
						</div>

						<!-- Suggestions Dropdown -->
						<div
							v-if="
								suggestions.length > 0 ||
								loading ||
								(searchHistory.length > 0 && !query)
							"
							class="suggestions-list"
						>
							<!-- Loading State -->
							<div v-if="loading" class="suggestion-item loading">
								<div class="spinner"></div>
								Cercando...
							</div>

							<!-- Live Suggestions -->
							<template v-else-if="suggestions.length > 0">
								<div
									v-for="(item, index) in suggestions"
									:key="index"
									class="suggestion-item"
									@click="selectSuggestion(item)"
								>
									<div class="icon-wrapper">
										<MapPin :size="18" />
									</div>
									<div class="suggestion-text">
										<span class="main-text">{{ formatMainText(item) }}</span>
										<span class="sub-text">{{ formatSubText(item) }}</span>
									</div>
								</div>
							</template>

							<!-- History Items -->
							<template v-else-if="searchHistory.length > 0 && !query">
								<div class="history-header">
									<span>Recenti</span>
									<button @click.stop="clearHistory" class="clear-history-btn">
										Cancella tutto
									</button>
								</div>
								<div
									v-for="(item, index) in searchHistory"
									:key="'hist-' + index"
									class="suggestion-item history"
								>
									<div class="history-content" @click="selectHistoryItem(item)">
										<div class="icon-wrapper history">
											<History :size="18" />
										</div>
										<div class="suggestion-text">
											<span class="main-text">{{
												item.display_name.split(",")[0]
											}}</span>
											<span class="sub-text">{{ item.display_name }}</span>
										</div>
									</div>
									<button
										class="remove-item-btn"
										@click.stop="removeFromHistory(index)"
										title="Rimuovi"
									>
										<X :size="14" />
									</button>
								</div>
							</template>
						</div>
					</div>
				</div>
			</Transition>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import debounce from "lodash/debounce";
import { Search, X, MapPin, History } from "lucide-vue-next";

interface NominatimResult {
	place_id: number;
	lat: string;
	lon: string;
	display_name: string;
	type: string;
	class: string;
	boundingbox?: string[];
}

const emit = defineEmits(["select-location"]);

const query = ref("");
const suggestions = ref<NominatimResult[]>([]);
const loading = ref(false);
const inputRef = ref<HTMLInputElement | null>(null);
const isExpanded = ref(false);

const searchHistory = ref<NominatimResult[]>([]);
const HISTORY_KEY = "map_search_history";

function openSearch() {
	isExpanded.value = true;
	nextTick(() => {
		inputRef.value?.focus();
	});
}

function closeSearch() {
	isExpanded.value = false;
	suggestions.value = [];
}

// Init History
try {
	const saved = localStorage.getItem(HISTORY_KEY);
	if (saved) {
		searchHistory.value = JSON.parse(saved);
	}
} catch (e) {
	console.warn("Failed to load search history", e);
}

function addToHistory(item: NominatimResult) {
	searchHistory.value = searchHistory.value.filter(
		(h) => h.place_id !== item.place_id && h.display_name !== item.display_name,
	);
	searchHistory.value.unshift(item);
	if (searchHistory.value.length > 5) {
		searchHistory.value = searchHistory.value.slice(0, 5);
	}
	localStorage.setItem(HISTORY_KEY, JSON.stringify(searchHistory.value));
}

function clearHistory() {
	searchHistory.value = [];
	localStorage.removeItem(HISTORY_KEY);
}

function removeFromHistory(index: number) {
	searchHistory.value.splice(index, 1);
	localStorage.setItem(HISTORY_KEY, JSON.stringify(searchHistory.value));
}

function selectHistoryItem(item: NominatimResult) {
	selectSuggestion(item, false);
	addToHistory(item);
}

const searchNominatim = async (q: string) => {
	if (!q || q.length < 3) {
		suggestions.value = [];
		return;
	}
	loading.value = true;
	try {
		const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
			q,
		)}&limit=5&addressdetails=1`;
		const res = await fetch(url);
		if (res.ok) {
			suggestions.value = await res.json();
		}
	} catch (e) {
		console.error("Nominatim search error", e);
	} finally {
		loading.value = false;
	}
};

const debouncedSearch = debounce(searchNominatim, 500);

watch(query, (newVal) => {
	const val = newVal.trim();
	if (isCoordinates(val)) {
		suggestions.value = [];
		return;
	}
	if (!val || val.length < 3) {
		suggestions.value = [];
		return;
	}
	debouncedSearch(val);
});

function isCoordinates(text: string): boolean {
	return /^-?\d+(\.\d+)?[\s,]+-?\d+(\.\d+)?$/.test(text);
}

function handleEnter() {
	const val = query.value.trim();
	if (isCoordinates(val)) {
		const parts = val.split(/[\s,]+/);
		const lat = parseFloat(parts[0]);
		const lng = parseFloat(parts[1]);
		if (!isNaN(lat) && !isNaN(lng)) {
			emit("select-location", {
				lat,
				lng,
				label: `Coordinate: ${lat}, ${lng}`,
			});
			closeSearch();
		}
	} else if (suggestions.value.length > 0) {
		selectSuggestion(suggestions.value[0]);
	} else {
		searchNominatim(val);
	}
}

function selectSuggestion(item: NominatimResult, saveToHistory = true) {
	let bbox: [number, number, number, number] | undefined;
	if (item.boundingbox && item.boundingbox.length === 4) {
		bbox = [
			parseFloat(item.boundingbox[2]),
			parseFloat(item.boundingbox[0]),
			parseFloat(item.boundingbox[3]),
			parseFloat(item.boundingbox[1]),
		];
	}
	emit("select-location", {
		lat: parseFloat(item.lat),
		lng: parseFloat(item.lon),
		label: item.display_name,
		bbox,
	});
	query.value = item.display_name;
	closeSearch();
	if (saveToHistory) {
		addToHistory(item);
	}
}

function clearSearch() {
	query.value = "";
	suggestions.value = [];
	inputRef.value?.focus();
}

function formatMainText(item: NominatimResult) {
	const parts = item.display_name.split(",");
	return parts[0];
}
function formatSubText(item: NominatimResult) {
	const parts = item.display_name.split(",");
	if (parts.length > 1) {
		return parts.slice(1).join(",").trim();
	}
	return "";
}
</script>

<style scoped>
.map-search-wrapper {
	display: flex;
	align-items: center;
}

.search-trigger-btn {
	width: 40px;
	height: 40px;
	background: white;
	border: 2px solid rgba(0, 0, 0, 0.2);
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #333;
	cursor: pointer;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
	transition: 0.2s;
}

.search-trigger-btn:hover {
	background: #f5f5f5;
	transform: translateY(-1px);
}

.search-panel-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	background: rgba(0, 0, 0, 0.4);
	backdrop-filter: blur(8px);
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding-top: 10vh;
	z-index: 100000;
}

.search-container {
	width: 90%;
	max-width: 600px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	border-radius: 10px;
}

.search-input-wrapper {
	background: rgba(26, 32, 38, 0.95);
	backdrop-filter: blur(20px);
	border: 1px solid rgba(255, 255, 255, 0.12);
	border-radius: 16px;
	display: flex;
	align-items: center;
	padding: 12px 20px;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
	transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-input-wrapper:focus-within {
	border-color: #2196f3;
	background: rgba(30, 36, 42, 0.98);
	box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.15), 0 20px 50px rgba(0, 0, 0, 0.6);
}

.search-icon {
	margin-right: 16px;
	color: #2196f3;
}

.search-input {
	background: transparent;
	border: none;
	color: white;
	flex: 1;
	font-size: 1.1rem;
	height: 44px;
	font-weight: 400;
}

.search-input:focus {
	outline: none;
}

.search-input::placeholder {
	color: rgba(255, 255, 255, 0.25);
}

.divider {
	width: 1px;
	height: 28px;
	background: rgba(255, 255, 255, 0.1);
	margin: 0 16px;
}

.clear-btn,
.close-panel-btn {
	background: none;
	border: none;
	color: rgba(255, 255, 255, 0.4);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: 0.2s;
}

.clear-btn:hover,
.close-panel-btn:hover {
	color: white;
}

.suggestions-list {
	background: rgba(26, 32, 38, 0.95);
	backdrop-filter: blur(20px);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 16px;
	max-height: 50vh;
	overflow-y: auto;
	box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
}

.suggestion-item {
	padding: 16px 20px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.04);
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 16px;
	transition: 0.2s;
}

.suggestion-item:last-child {
	border-bottom: none;
}

.suggestion-item:hover {
	background: rgba(255, 255, 255, 0.05);
}

.icon-wrapper {
	width: 40px;
	height: 40px;
	border-radius: 12px;
	background: rgba(33, 150, 243, 0.1);
	display: flex;
	align-items: center;
	justify-content: center;
	color: #2196f3;
	flex-shrink: 0;
}

.icon-wrapper.history {
	background: rgba(255, 255, 255, 0.05);
	color: rgba(255, 255, 255, 0.4);
}

.history-content {
	display: flex;
	align-items: center;
	gap: 16px;
	flex: 1;
	overflow: hidden;
}

.suggestion-text {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	flex: 1;
}

.main-text {
	font-size: 1rem;
	font-weight: 500;
	color: #eee;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.sub-text {
	font-size: 0.85rem;
	color: rgba(255, 255, 255, 0.4);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-top: 2px;
}

.history-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 20px;
	font-size: 0.75rem;
	color: rgba(255, 255, 255, 0.2);
	background: rgba(255, 255, 255, 0.01);
	text-transform: uppercase;
	letter-spacing: 1.5px;
	font-weight: 600;
}

.clear-history-btn {
	background: none;
	border: none;
	color: #ef5350;
	cursor: pointer;
	font-size: 0.75rem;
	font-weight: 600;
}

.remove-item-btn {
	background: none;
	border: none;
	color: rgba(255, 255, 255, 0.15);
	cursor: pointer;
	padding: 8px;
	transition: 0.2s;
}

.remove-item-btn:hover {
	color: #ef5350;
}

.suggestion-item.loading {
	justify-content: center;
	padding: 40px;
	color: rgba(255, 255, 255, 0.3);
	gap: 16px;
	font-size: 1rem;
}

.spinner {
	width: 24px;
	height: 24px;
	border: 3px solid rgba(255, 255, 255, 0.1);
	border-top-color: #2196f3;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* Transitions */
.search-panel-fade-enter-active,
.search-panel-fade-leave-active {
	transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-panel-fade-enter-from,
.search-panel-fade-leave-to {
	opacity: 0;
	backdrop-filter: blur(0px);
}

.search-panel-fade-enter-from .search-container {
	transform: translateY(-40px) scale(0.95);
}

.search-panel-fade-leave-to .search-container {
	transform: translateY(-20px) scale(0.98);
}
</style>
